import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, updateDoc, writeBatch, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    Box,
    TextField,
    IconButton,
    Paper,
    Typography,
    Avatar,
    AppBar,
    Toolbar,
    Alert,
    Button
} from '@mui/material';
import { Send as SendIcon, ArrowBack as ArrowBackIcon, ChatBubbleOutline as ChatBubbleOutlineIcon, Phone as PhoneIcon } from '@mui/icons-material';
import { UserAvatar } from '../components/common/UserAvatar';
import { getUser } from '../services/api';
import { keyframes } from '@mui/system';
import PhoneVerification from '../components/auth/PhoneVerification';

// レート制限の設定
const MESSAGE_LIMIT = 20; // 1分間に送信できるメッセージ数
const WINDOW_MS = 60 * 1000; // 1分間

// アニメーションの定義
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

function ChatRoom() {
    const { chatRoomId } = useParams();
    const { currentUser, userProfile, checkPhoneVerification } = useAuth();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [otherUser, setOtherUser] = useState(null);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [sending, setSending] = useState(false);
    const [showPhoneVerification, setShowPhoneVerification] = useState(false);

    const DEFAULT_AVATAR = process.env.PUBLIC_URL + '/images/default-avatar.jpg';

    // 電話番号認証チェック
    const isPhoneVerified = userProfile?.phoneVerified || false;

    useEffect(() => {
        if (!chatRoomId || !currentUser) return;

        // 相手のユーザー情報を取得
        const fetchOtherUser = async () => {
            try {
                const chatRoomRef = doc(db, 'chatRooms', chatRoomId);
                const chatRoomSnap = await getDoc(chatRoomRef);
                if (chatRoomSnap.exists()) {
                    const data = chatRoomSnap.data();
                    const otherUserId = data.participants.find(id => id !== currentUser.uid);

                    // キャッシュ機能付きでユーザー情報を取得
                    const userData = await getUser(otherUserId);
                    setOtherUser(userData);

                    // 未読メッセージを既読に更新
                    const batch = writeBatch(db);

                    // チャットルームの未読カウントをリセット
                    const unreadCounts = data.unreadCounts || {};
                    if (unreadCounts[currentUser.uid]) {
                        unreadCounts[currentUser.uid] = 0;
                        batch.update(chatRoomRef, { unreadCounts });
                    }

                    // メッセージを既読に更新
                    const messagesRef = collection(db, 'chatRooms', chatRoomId, 'messages');
                    const unreadQuery = query(
                        messagesRef,
                        where('senderId', '==', otherUserId),
                        where('read', '==', false)
                    );
                    const unreadSnapshot = await getDocs(unreadQuery);

                    unreadSnapshot.docs.forEach(doc => {
                        batch.update(doc.ref, { read: true });
                    });

                    await batch.commit();
                } else {
                    setError('会話情報が見つかりません');
                }
            } catch (error) {
                console.error('ユーザー情報取得エラー:', error);
                setError('ユーザー情報の取得に失敗しました');
            }
        };

        fetchOtherUser();

        // メッセージのリアルタイム取得
        const messagesRef = collection(db, 'chatRooms', chatRoomId, 'messages');
        const q = query(messagesRef, orderBy('createdAt', 'asc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const newMessages = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMessages(newMessages);
        });

        return () => unsubscribe();
    }, [chatRoomId, currentUser]);

    useLayoutEffect(() => {
        if (messages.length > 0 && messagesContainerRef.current) {
            // 固定位置コンテナで最下部にスクロール
            const container = messagesContainerRef.current;
            container.scrollTop = container.scrollHeight;
        }
    }, [messages]);

    // レート制限チェック関数
    const checkRateLimit = () => {
        const now = Date.now();

        // ローカルストレージからメッセージ履歴を取得
        let messageHistory = JSON.parse(localStorage.getItem(`msg_history_${currentUser.uid}`) || '[]');

        // 古いタイムスタンプを削除
        messageHistory = messageHistory.filter(timestamp => now - timestamp < WINDOW_MS);

        // 制限チェック
        if (messageHistory.length >= MESSAGE_LIMIT) {
            throw new Error('メッセージの送信頻度が高すぎます。しばらく待ってから再試行してください。');
        }

        // 新しいタイムスタンプを追加して保存
        messageHistory.push(now);
        localStorage.setItem(`msg_history_${currentUser.uid}`, JSON.stringify(messageHistory));
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || sending) return;

        // 電話番号認証チェック
        if (!isPhoneVerified) {
            setError('メッセージを送信するには電話番号認証が必要です');
            return;
        }

        try {
            setSending(true);
            setError('');

            // レート制限チェック
            checkRateLimit();

            const roomRef = doc(db, 'chatRooms', chatRoomId);
            const roomSnap = await getDoc(roomRef);
            const roomData = roomSnap.data();
            const otherUserId = roomData.participants.find(id => id !== currentUser.uid);
            const messagesRef = collection(db, 'chatRooms', chatRoomId, 'messages');

            // バッチ処理の開始
            const batch = writeBatch(db);

            // メッセージの追加
            const messageRef = doc(messagesRef);
            batch.set(messageRef, {
                text: newMessage,
                senderId: currentUser.uid,
                createdAt: serverTimestamp(),
                read: false
            });

            // チャットルームの最終メッセージ情報と未読カウントを更新
            const unreadCounts = roomData.unreadCounts || {};
            unreadCounts[otherUserId] = (unreadCounts[otherUserId] || 0) + 1;

            batch.update(roomRef, {
                lastMessage: newMessage,
                lastMessageTime: serverTimestamp(),
                lastMessageSenderId: currentUser.uid,
                updatedAt: serverTimestamp(),
                unreadCounts
            });

            await batch.commit();

            setNewMessage('');
        } catch (error) {
            console.error('メッセージの送信に失敗:', error);
            setError('メッセージ送信中にエラーが発生しました: ' + error.message);
        } finally {
            setSending(false);
        }
    };

    const handleAvatarClick = (userId) => {
        if (userId) {
            navigate(`/users/${userId}`);
        }
    };

    // 定期的なメッセージ履歴のクリーンアップ
    useEffect(() => {
        const cleanupInterval = setInterval(() => {
            if (!currentUser) return;

            const now = Date.now();
            const storageKey = `msg_history_${currentUser.uid}`;
            let messageHistory = JSON.parse(localStorage.getItem(storageKey) || '[]');

            // 1分以上前のタイムスタンプを削除
            messageHistory = messageHistory.filter(timestamp => now - timestamp < WINDOW_MS);

            // 更新された配列を保存
            localStorage.setItem(storageKey, JSON.stringify(messageHistory));
        }, 30000); // 30秒ごとにクリーンアップ

        return () => clearInterval(cleanupInterval);
    }, [currentUser]);

    // 電話番号認証成功時の処理
    const handlePhoneVerificationSuccess = async () => {
        await checkPhoneVerification();
        setError('');
    };

    const MessageItem = ({ message }) => {
        const isOwnMessage = message.senderId === currentUser.uid;

        if (!isOwnMessage && !otherUser) {
            return null;
        }

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isOwnMessage ? 'row-reverse' : 'row',
                    alignItems: 'flex-end',
                    mb: 2,
                    gap: 1
                }}
            >
                {!isOwnMessage && (
                    <Box
                        onClick={() => handleAvatarClick(otherUser?._id)}
                        sx={{
                            cursor: 'pointer',
                            '&:hover': {
                                opacity: 0.8
                            }
                        }}
                    >
                        <UserAvatar
                            user={otherUser}
                            size={32}
                        />
                    </Box>
                )}
                <Box
                    sx={{
                        maxWidth: '70%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isOwnMessage ? 'flex-end' : 'flex-start'
                    }}
                >
                    {!isOwnMessage && otherUser && (
                        <Typography variant="caption" sx={{ ml: 1, mb: 0.5 }}>
                            {otherUser.profile.nickname}
                        </Typography>
                    )}
                    <Paper
                        sx={{
                            p: 1.5,
                            backgroundColor: isOwnMessage ? 'primary.main' : 'grey.100',
                            color: isOwnMessage ? 'white' : 'text.primary',
                            borderRadius: 2
                        }}
                    >
                        <Typography variant="body1">{message.text}</Typography>
                    </Paper>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Typography variant="caption" color="textSecondary">
                            {formatTime(message.createdAt?.toDate())}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    };

    const formatTime = (date) => {
        if (!date) return '';
        return new Intl.DateTimeFormat('ja-JP', {
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <AppBar position="fixed" sx={{ bgcolor: 'background.paper', zIndex: 1100 }}>
                <Toolbar sx={{ gap: 2 }}>
                    <IconButton
                        edge="start"
                        onClick={() => navigate('/messages')}
                        sx={{
                            color: 'text.primary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flex: 1,
                            gap: 2
                        }}
                    >
                        <Box
                            onClick={() => handleAvatarClick(otherUser?._id)}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                    opacity: 0.8
                                }
                            }}
                        >
                            <UserAvatar user={otherUser} />
                        </Box>
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'text.primary',
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}
                        >
                            {otherUser?.profile?.nickname || '不明なユーザー'}
                        </Typography>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* 電話番号認証が必要な場合の警告 */}
            {!isPhoneVerified && (
                <Alert
                    severity="warning"
                    sx={{
                        position: 'fixed',
                        top: 64, // AppBarの高さ分
                        left: 0,
                        right: 0,
                        mx: 0,
                        borderRadius: 0,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        zIndex: 1050
                    }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            startIcon={<PhoneIcon />}
                            onClick={() => setShowPhoneVerification(true)}
                        >
                            認証する
                        </Button>
                    }
                >
                    メッセージを送信するには電話番号認証が必要です
                </Alert>
            )}

            {/* エラーメッセージを上部に表示 */}
            {error && (
                <Paper
                    sx={{
                        p: 2,
                        mx: 2,
                        mt: !isPhoneVerified ? 15 : 1, // 電話番号認証Alert分のスペースを考慮（さらに下に）
                        bgcolor: 'error.light',
                        color: 'error.contrastText',
                        borderRadius: 1
                    }}
                >
                    <Typography variant="body2">
                        {error}
                    </Typography>
                </Paper>
            )}

            {/* メッセージ一覧 */}
            <Box
                ref={messagesContainerRef}
                sx={{
                    position: 'fixed',
                    top: !isPhoneVerified ? 120 : 64, // AppBar + Alert(表示時のみ)の下から開始
                    left: 0,
                    right: 0,
                    bottom: 80, // 送信ボックスの上まで
                    p: 2,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    zIndex: 1
                }}
            >
                {messages.length > 0 ? (
                    <>
                        {messages.map((message) => (
                            <MessageItem key={message.id} message={message} />
                        ))}
                        <div ref={messagesEndRef} />
                    </>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            textAlign: 'center',
                            px: 2
                        }}
                    >
                        <Box
                            sx={{
                                animation: `${floatAnimation} 3s ease-in-out infinite`,
                                mb: 2
                            }}
                        >
                            <ChatBubbleOutlineIcon
                                sx={{
                                    fontSize: '3rem',
                                    color: 'primary.main',
                                    opacity: 0.6
                                }}
                            />
                        </Box>
                        <Typography
                            variant="h6"
                            component="h2"
                            sx={{
                                fontWeight: 'medium',
                                mb: 1,
                                color: 'text.primary'
                            }}
                        >
                            メッセージはまだありません
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ maxWidth: '300px', mx: 'auto' }}
                        >
                            メッセージを送信して、会話を始めましょう。
                        </Typography>
                    </Box>
                )}
            </Box>

            <Paper
                component="form"
                onSubmit={handleSendMessage}
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 2,
                    display: 'flex',
                    gap: 1,
                    boxShadow: 3,
                    backgroundColor: 'background.paper',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0,
                    paddingBottom: 'calc(16px + env(safe-area-inset-bottom))', // スマホのブラウザUIに対応
                    zIndex: 1000
                }}
            >
                <TextField
                    fullWidth
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={isPhoneVerified ? "メッセージを入力..." : "電話番号認証が必要です"}
                    variant="outlined"
                    size="small"
                    disabled={sending || !isPhoneVerified}
                />
                <IconButton
                    type="submit"
                    color="primary"
                    disabled={!newMessage.trim() || sending || !isPhoneVerified}
                >
                    <SendIcon />
                </IconButton>
            </Paper>

            <PhoneVerification
                open={showPhoneVerification}
                onClose={() => setShowPhoneVerification(false)}
                onSuccess={handlePhoneVerificationSuccess}
            />
        </Box>
    );
}

export default ChatRoom; 